import { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Package, Boxes, Warehouse, PackagePlus, ClipboardList,
  ShoppingCart, ClipboardCheck, Undo2, Building2, LineChart,
  Settings, LogOut, Menu, X, FileSpreadsheet, Leaf, Store, FileText, Tag, UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n, LOCALES } from "@/i18n";

const NAV = [
  { section: "nav.overview", items: [
    { to: "/", label: "nav.dashboard", icon: LayoutDashboard, end: true },
  ]},
  { section: "nav.catalog", items: [
    { to: "/produkte", label: "nav.products", icon: Package },
    { to: "/bundles", label: "nav.bundles", icon: Boxes },
    { to: "/sales-aktionen", label: "sp.tab", icon: Tag },
  ]},
  { section: "nav.warehouse", items: [
    { to: "/lager", label: "nav.inventory", icon: Warehouse },
    { to: "/wareneingang", label: "nav.receiving", icon: PackagePlus },
    { to: "/verpackung", label: "nav.packaging", icon: Boxes },
    { to: "/inventur", label: "nav.stocktake", icon: ClipboardList },
  ]},
  { section: "nav.orders", items: [
    { to: "/auftraege", label: "nav.orders", icon: ShoppingCart },
    { to: "/messe", label: "nav.fairSales", icon: Store },
    { to: "/pick-pack", label: "nav.pickPack", icon: ClipboardCheck },
    { to: "/retouren", label: "nav.returns", icon: Undo2 },
    { to: "/b2b", label: "nav.b2b", icon: Building2 },
  ]},
  { section: "nav.analysis", items: [
    { to: "/finanzen", label: "nav.finance", icon: LineChart },
    { to: "/import", label: "nav.excelImport", icon: FileSpreadsheet },
    { to: "/rechnungsimport", label: "nav.invoiceImport", icon: FileText },
    { to: "/einstellungen", label: "nav.settings", icon: Settings },
  ]},
];

const TEST_ID = {
  "nav.dashboard": "dashboard", "nav.products": "produkte", "nav.bundles": "bundles",
  "nav.inventory": "lager-bersicht", "nav.receiving": "wareneingang", "nav.packaging": "verpackung",
  "nav.stocktake": "inventur", "sp.tab": "sales-aktionen",
  "nav.orders": "auftr-ge", "nav.fairSales": "messe-verk-ufe", "nav.pickPack": "pick---pack",
  "nav.returns": "retouren", "nav.b2b": "b b-kunden", "nav.finance": "finanzauswertung",
  "nav.shopify": "shopify-sync", "nav.excelImport": "excel-import",
  "nav.invoiceImport": "rechnungs-import", "nav.settings": "einstellungen", "nav.tenants": "mandanten",
};

const ROLE_KEY = { admin: "role.admin", lager: "role.lager", verkauf: "role.verkauf", disponent: "role.disponent" };

// Rollen-Sichtbarkeit der Navigation (zusätzliche UX-Sperre — maßgeblich ist immer der Server).
// Der Disponent arbeitet ausschließlich im Lager und darf Aufträge nur lesen.
const DISPONENT_ALLOWED = ["/", "/produkte", "/bundles", "/lager", "/wareneingang", "/verpackung",
  "/inventur", "/eigenentnahmen", "/promotion", "/auftraege", "/pick-pack", "/retouren",
  "/finanzen"];
// Lager: operative Lagerarbeit ohne Finanz-/Margendaten (kein Finanzbereich, keine Eigenentnahmen,
// keine Promotionen, keine Importe, keine Einstellungen).
const LAGER_ALLOWED = ["/", "/produkte", "/bundles", "/lager", "/wareneingang", "/verpackung",
  "/inventur", "/auftraege", "/messe", "/pick-pack", "/retouren"];
// Verkauf: Vertriebssicht inkl. B2B, Eigenentnahmen und Promotionen — ohne Finanzauswertung,
// Shopify-Sync, Importe und Einstellungen.
const VERKAUF_ALLOWED = ["/", "/produkte", "/bundles", "/lager", "/wareneingang", "/verpackung",
  "/inventur", "/eigenentnahmen", "/promotion", "/auftraege", "/messe", "/pick-pack", "/retouren",
  "/b2b"];
const ROLE_NAV = { disponent: DISPONENT_ALLOWED, lager: LAGER_ALLOWED, verkauf: VERKAUF_ALLOWED };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, locale, setLocale, enabled, currency } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const roleLabel = t(ROLE_KEY[user?.role] || "", user?.role);
  const langOptions = useMemo(() => LOCALES.filter((l) => enabled.includes(l.locale)), [enabled]);
  const nav = useMemo(() => {
    const allowed = ["superadmin", "dev"].includes(user?.platform_role) ? null : ROLE_NAV[user?.role];
    const base = allowed
      ? NAV.map((g) => ({ ...g, items: g.items.filter((i) => allowed.includes(i.to)) }))
        .filter((g) => g.items.length)
      : NAV;
    // Der Excel-Import ist eine Sonderfunktion des Mandanten Gluty-Free Club und der Betreiber.
    const excelAllowed = user?.tenant?.code === "GFC" || user?.tenant?.slug === "gfc"
      || ["superadmin", "dev"].includes(user?.platform_role);
    const filtered = excelAllowed ? base
      : base.map((g) => ({ ...g, items: g.items.filter((i) => i.to !== "/import") }))
        .filter((g) => g.items.length);
    return ["superadmin", "dev"].includes(user?.platform_role)
      ? [...filtered, { section: "nav.operator", items: [{ to: "/mandanten", label: "nav.tenants", icon: Store }] }]
      : filtered;
  }, [user]);

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 py-5 border-b border-border">
        <img src="/logo.webp" alt="Gluty-Free Club" className="h-9 w-auto" />
        <div>
          <div className="font-heading text-base font-bold leading-tight">Club Operations</div>
          <div className="text-[11px] text-muted-foreground">powered by consberry</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {nav.map((grp) => (
          <div key={grp.section}>
            <div className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t(grp.section)}</div>
            <div className="space-y-0.5">
              {grp.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)}
                  data-testid={`nav-${TEST_ID[it.label] || it.label}`}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors duration-150",
                    isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-muted hover:text-foreground"
                  )}>
                  <it.icon className="h-4 w-4 shrink-0" />
                  {t(it.label)}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-md px-2 py-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-xs font-bold">
            {(user?.name || "?").slice(0, 1)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground">{roleLabel}</div>
          </div>
          <button onClick={() => navigate("/profil")} data-testid="profile-btn" title={t("nav.profile")}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <UserCog className="h-4 w-4" />
          </button>
          <button data-testid="logout-btn" title={t("nav.logout")} onClick={async () => { await logout(); navigate("/login"); }}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-destructive transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card">{SidebarInner}</aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-card border-r border-border">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-border bg-card/80 px-4 py-3 lg:px-8 backdrop-blur">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground" data-testid="tenant-branding">
              {user?.tenant?.logo_url ? <img src={user.tenant.logo_url} alt="" className="h-5 w-auto rounded" /> : null}
              {user?.tenant?.name || t("nav.internalTool")} · {currency}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {enabled.length > 1 && (
              <select value={locale} onChange={(e) => setLocale(e.target.value)} data-testid="language-switcher"
                className="rounded-full border border-border bg-transparent px-2.5 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-primary">
                {langOptions.map((l) => (
                  <option key={l.locale} value={l.locale}>{l.endonym}</option>))}
              </select>
            )}
            <span className="rounded-full border border-border px-3 py-1 text-xs font-medium">{roleLabel}</span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
